/*
 * The sales claim hold: any non-terminal task is claimed on open and held for
 * an idle window that only server-side activity (call started, message sent,
 * outcome logged) extends. The form learns of extensions, releases and
 * re-claims by polling GET /claim_status, so the remaining time is always
 * derived from the server's absolute expiry, never counted down locally.
 */

import { is_terminal as isTerminal } from '../../util_components/instant_alert/task_status';
import {
  CLAIM_BLOCKED,
  CLAIM_EXPIRED,
  CLAIM_MINE,
  CLAIM_RELEASED,
  CLAIM_TERMINAL
} from '../../util_components/instant_alert/claim';
import type {
  ClaimState,
  ClaimableForm,
  ReleasedTask
} from '../../util_components/instant_alert/claim';
import type { TimerColor } from '../../util_components/instant_alert/instant_timer';
import type { TeamMember } from '../sales_team/api';

export const CLAIM_POLL_MS = 30 * 1000;
export const HOLD_WARN_MS = 5 * 60 * 1000;
export const HOLD_CRITICAL_MS = 60 * 1000;

export type HoldPhase = 'ok' | 'warn' | 'critical' | 'expired';

// GET /v2/sales_task_list/claim_status/{uuid} `data`.
export interface ClaimStatusPoll {
  claimed_by_ops_uuid?: string;
  claimed_by_name?: string;
  claim_expires_at_iso?: string | null;
  server_now_iso?: string;
  status?: string;
  held_by_me?: boolean;
}

// POST /assign `data`.
export interface AssignResult {
  claimed_by_ops_uuid?: string;
  claimed_by_name?: string;
  claim_expires_at_iso?: string | null;
}

// No instant gate: every open, non-terminal sales task is claimed.
export const should_claim_any_on_open = (form: ClaimableForm | null | undefined): boolean =>
  !!form && !isTerminal(form);

const parse_ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
};

// Server clock minus local clock, measured when server_now_iso arrived.
export const clock_skew_ms = (
  server_now_iso: string | null | undefined,
  received_at_ms: number | null | undefined
): number => {
  const server = parse_ms(server_now_iso);
  if (server === null || typeof received_at_ms !== 'number') return 0;
  return server - received_at_ms;
};

// Infinity when there is no expiry (held without a timer).
export const hold_remaining_ms = (
  expires_iso: string | null | undefined,
  now_ms: number,
  skew_ms = 0
): number => {
  const expires = parse_ms(expires_iso);
  if (expires === null) return Infinity;
  return Math.max(0, expires - (now_ms + skew_ms));
};

export const claim_remaining_ms = (
  claim: Pick<ClaimState, 'claim_expires_at_iso' | 'server_now_iso' | 'received_at_ms'>,
  now_ms: number
): number =>
  hold_remaining_ms(
    claim.claim_expires_at_iso,
    now_ms,
    clock_skew_ms(claim.server_now_iso, claim.received_at_ms)
  );

export const hold_phase = (remaining_ms: number): HoldPhase => {
  if (remaining_ms > HOLD_WARN_MS) return 'ok';
  if (remaining_ms > HOLD_CRITICAL_MS) return 'warn';
  if (remaining_ms > 0) return 'critical';
  return 'expired';
};

export const PHASE_COLOR: Record<HoldPhase, TimerColor> = {
  ok: 'black',
  warn: 'amber',
  critical: 'red',
  expired: 'red'
};

const pad = (n: number): string => String(n).padStart(2, '0');

// M:SS, whole minutes uncapped.
export const format_hold = (remaining_ms: number): string => {
  if (!Number.isFinite(remaining_ms)) return '';
  const total = Math.ceil(Math.max(0, remaining_ms) / 1000);
  return `${Math.floor(total / 60)}:${pad(total % 60)}`;
};

const base = (state: ClaimState['state'], poll: ClaimStatusPoll, reason = ''): ClaimState => ({
  state,
  claimed_by_ops_uuid: poll.claimed_by_ops_uuid || '',
  claimed_by_name: poll.claimed_by_name || '',
  reason
});

export const state_from_claim_status = (
  poll: ClaimStatusPoll | null | undefined,
  me_uuid: string,
  received_at_ms: number = Date.now()
): ClaimState => {
  const p = poll || {};
  if (isTerminal(p)) return base(CLAIM_TERMINAL, p);
  const holder = p.claimed_by_ops_uuid || '';
  const mine = p.held_by_me === true || (!!holder && holder === me_uuid);
  if (mine) {
    const expires = parse_ms(p.claim_expires_at_iso);
    const server_now = parse_ms(p.server_now_iso);
    const lapsed = expires !== null && server_now !== null && expires <= server_now;
    return {
      ...base(lapsed ? CLAIM_EXPIRED : CLAIM_MINE, p),
      claim_expires_at_iso: p.claim_expires_at_iso || null,
      server_now_iso: p.server_now_iso,
      received_at_ms
    };
  }
  if (!holder) return base(CLAIM_RELEASED, p);
  return base(CLAIM_BLOCKED, p, `Now held by ${p.claimed_by_name || 'another rep'}`);
};

// After POST /assign: assigning to oneself keeps the hold; anyone else takes it.
export const state_after_assign = (
  res: AssignResult | null | undefined,
  me_uuid: string
): ClaimState => {
  const r = res || {};
  if (r.claimed_by_ops_uuid && r.claimed_by_ops_uuid === me_uuid) {
    return {
      ...base(CLAIM_MINE, r),
      claim_expires_at_iso: r.claim_expires_at_iso || null
    };
  }
  return base(CLAIM_BLOCKED, r, `Assigned to ${r.claimed_by_name || 'another rep'}`);
};

export const released_toast = (task: ReleasedTask | null | undefined): string => {
  if (!task?.uuid) return '';
  const what = task.task_label || 'your previous task';
  return task.student_name
    ? `Released "${what}" for ${task.student_name} back to the pool`
    : `Released "${what}" back to the pool`;
};

export const hold_lapsed = (claim: ClaimState, now_ms: number): boolean =>
  claim.state === CLAIM_MINE && claim_remaining_ms(claim, now_ms) <= 0;

export const hold_forces_read_only = (
  claim: ClaimState | null | undefined,
  now_ms?: number
): boolean => {
  if (!claim) return false;
  if (claim.state === CLAIM_EXPIRED || claim.state === CLAIM_RELEASED) return true;
  return typeof now_ms === 'number' && hold_lapsed(claim, now_ms);
};

export const shows_hold_banner = (claim: ClaimState | null | undefined): boolean =>
  !!claim &&
  (claim.state === CLAIM_MINE || claim.state === CLAIM_EXPIRED || claim.state === CLAIM_RELEASED);

// Leave and shift are shown, not enforced: a manager may still hand a task to
// someone about to start their shift.
// goserver lists SALES users without a team row as ACTIVE but unconfigured;
// /assign rejects them with ASSIGNEE_NOT_ACTIVE.
const is_assignable = (m: TeamMember): boolean => m.status === 'ACTIVE' && m.configured;

export const unconfigured_count = (members: TeamMember[]): number =>
  members.filter((m) => m.status === 'ACTIVE' && !m.configured).length;

export const unconfigured_hint = (n: number): string =>
  n === 1
    ? '1 user with the SALES role is not on the Sales Team yet'
    : `${n} users with the SALES role are not on the Sales Team yet`;

export const assignable_members = (members: TeamMember[], query: string): TeamMember[] => {
  const q = query.trim().toLowerCase();
  return members
    .filter(is_assignable)
    .filter(
      (m) =>
        !q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    )
    .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
};
